// ========================= OutstationForm.jsx =========================
import React, { useEffect, useState, useRef, useCallback, useMemo } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import GooglePlacesAutocomplete from "react-google-places-autocomplete"
import { useSelector, useDispatch } from "react-redux"
import { getCabs } from "../../../../../store/Cab/CabAction"
import { toast } from "react-toastify"
import { getPlaceDetailsByPlaceId } from "../../../../../utils/placeDetails"
import { isSameLocation } from "../../../../../utils/locationValidation"
import { getDefaultOptions, getDefaultOptionsSync } from "../../../../../utils/defaultCityOptions"
import { saveRecentSearch, saveRecentTrip } from "../../../../../utils/recentSearches"
import { getRandomCity } from "../../../../../utils/cityStateMap"
import { slugify } from "../../../../../utils/slugify"
import { resolveRouteCityLabels } from "../../../../../utils/routeCityPrefill"
import { formatMobileDisplay, parseMobileInput, isValidMobileNumber } from "../../../../../utils/mobileNumberUtils"
import { DatePickerInput, TimePickerInput, DateTimeProvider } from "../../../../../components/DateTime"
import { geocodeCity } from "../../../../../utils/geocodeCity"
import { useMapsReady } from "../../../../../hooks/useMapsReady"

const formatSlugToCityName = (slug) => {
  if (!slug || typeof slug !== "string") return ""
  return slug
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ")
}

const OutstationForm = ({ routePrefill }) => {
  const [formValues, setFormValues] = useState({
    type: "one way",
    from: null,
    to: null,
    pickUpDate: "",
    pickUpTime: "",
    returnDate: "",
    returnTime: "",
    mobileNumber: "",
  })

  const [locationDetails, setLocationDetails] = useState({
    pickup_lat: null,
    pickup_long: null,
    pickup_city: "",
    pickup_fullAddress: "",
    drop_lat: null,
    drop_long: null,
    drop_city: "",
    drop_fullAddress: "",
  })

  const [showReturn, setShowReturn] = useState(false)

  const [defaultFromOptions, setDefaultFromOptions] = useState(() =>
    getDefaultOptionsSync("from")
  )
  const [defaultToOptions, setDefaultToOptions] = useState(() =>
    getDefaultOptionsSync("to")
  )
  const [fromMenuIsOpen, setFromMenuIsOpen] = useState(false)
  const [toMenuIsOpen, setToMenuIsOpen] = useState(false)

  const { location } = useSelector((state) => state.location)
  const routerLocation = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [searchLoading, setSearchLoading] = useState(false)
  const submitLockRef = useRef(false)
  const mapsReady = useMapsReady()

  const pickUpDateRef = useRef(null)
  const pickUpTimeRef = useRef(null)
  const returnDateRef = useRef(null)
  const returnTimeRef = useRef(null)

  const mobileNumberInputRef = useRef(null)
  const fromSelectRef = useRef(null)
  const toSelectRef = useRef(null)

  const locationInitializedRef = useRef(false)
  const hasInteractedRef = useRef(false)

  const today = new Date()
  const formattedToday = today.toLocaleDateString("en-CA")

  // ===================== Last cab booked logic (FINAL) =====================
  const MIN_CITY_CHANGE_MS = 8000 // 8 sec
  const MAX_CITY_CHANGE_MS = 120000 // 2 min
  const MIN_AGO_SEC = 3 // 3 sec
  const MAX_AGO_SEC = 120 // 2 min

  const randInt = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min

  const [randomCity, setRandomCity] = useState(() => getRandomCity())
  const [lastBookedSeconds, setLastBookedSeconds] = useState(() => randInt(MIN_AGO_SEC, MAX_AGO_SEC))

  const cityTimerRef = useRef(null)
  const tickTimerRef = useRef(null)

  useEffect(() => {
    // change city at random interval between 8s to 2min
    const scheduleNextCityChange = () => {
      const delay = randInt(MIN_CITY_CHANGE_MS, MAX_CITY_CHANGE_MS)

      cityTimerRef.current = setTimeout(() => {
        setRandomCity(getRandomCity())
        setLastBookedSeconds(randInt(MIN_AGO_SEC, MAX_AGO_SEC)) // reset counter on new city
        scheduleNextCityChange()
      }, delay)
    }

    scheduleNextCityChange()

    return () => {
      if (cityTimerRef.current) clearTimeout(cityTimerRef.current)
    }
  }, [])

  const lastBookedText =
    lastBookedSeconds >= 120
      ? "2 min ago"
      : lastBookedSeconds >= 60
        ? `${Math.floor(lastBookedSeconds / 60)} min ${lastBookedSeconds % 60} sec ago`
        : `${lastBookedSeconds} sec ago`
  // ===================== End Last cab booked logic =====================

  const getLabel = (v) => {
    if (!v) return ""
    if (typeof v === "string") return v
    return v?.fullAddress || v?.label || ""
  }

  const dispatchTripDataUpdated = () => window.dispatchEvent(new Event("tripDataUpdated"))

  const resolveCityToPlaceOption = useCallback((cityText) => {
    if (!cityText) return Promise.resolve(null)
    return Promise.resolve({
      label: cityText,
      value: { description: cityText },
    })
  }, [])

  // Prefill from/to when redirected from a route link (session guard or CityPair); API names when available
  useEffect(() => {
    if (!routePrefill?.fromCity || !routePrefill?.toCity) return
    let cancelled = false
    const fromSlug = routePrefill.fromCity
    const toSlug = routePrefill.toCity

    const applyNames = (fromName, toName) => {
      if (cancelled || !fromName || !toName) return
      locationInitializedRef.current = true
      setFormValues((prev) => ({
        ...prev,
        from: { label: fromName, value: { description: fromName } },
        to: { label: toName, value: { description: toName } },
      }))
      setLocationDetails((prev) => ({
        ...prev,
        pickup_city: fromName,
        pickup_fullAddress: fromName,
        drop_city: toName,
        drop_fullAddress: toName,
      }))
    }

    applyNames(formatSlugToCityName(fromSlug), formatSlugToCityName(toSlug))

    resolveRouteCityLabels(fromSlug, toSlug).then(({ from, to }) => {
      if (!cancelled) applyNames(from, to)
    })

    return () => {
      cancelled = true
    }
  }, [routePrefill?.fromCity, routePrefill?.toCity])

  // A route prefill hands us city NAMES only. The fare API prices by
  // coordinates, so without lat/long the search dead-ends ("No cabs / location
  // not proper") even though From/To look filled. Geocode both city names to
  // their centre once Maps is ready and fill the pickup/drop coordinates, so a
  // booking started from a route page prices city-to-city out of the box. Fails
  // silently: if geocoding is unavailable the form behaves exactly as before.
  useEffect(() => {
    if (!routePrefill?.fromCity || !routePrefill?.toCity || !mapsReady) return
    let cancelled = false
    const fromName = formatSlugToCityName(routePrefill.fromCity)
    const toName = formatSlugToCityName(routePrefill.toCity)

    Promise.all([geocodeCity(fromName), geocodeCity(toName)]).then(([f, t]) => {
      if (cancelled) return
      setLocationDetails((prev) => ({
        ...prev,
        ...(f ? { pickup_lat: f.lat, pickup_long: f.lng, pickup_city: prev.pickup_city || f.city } : {}),
        ...(t ? { drop_lat: t.lat, drop_long: t.lng, drop_city: prev.drop_city || t.city } : {}),
      }))
      setFormValues((prev) => ({
        ...prev,
        from: prev.from && f ? { ...prev.from, latitude: f.lat, longitude: f.lng, city: prev.from.city || f.city } : prev.from,
        to: prev.to && t ? { ...prev.to, latitude: t.lat, longitude: t.lng, city: prev.to.city || t.city } : prev.to,
      }))
    })

    return () => {
      cancelled = true
    }
  }, [routePrefill?.fromCity, routePrefill?.toCity, mapsReady])

  // Autofill FROM like your old logic (skip if routePrefill was applied)
  useEffect(() => {
    if (routePrefill?.fromCity && routePrefill?.toCity) return
    locationInitializedRef.current = false

    const run = async () => {
      if (locationInitializedRef.current) return

      if (routerLocation.pathname === "/" && location?.city) {
        const option = await resolveCityToPlaceOption(location.city)
        if (option) {
          locationInitializedRef.current = true
          setFormValues((prev) => ({ ...prev, from: option }))
        }
        return
      }

      if (routerLocation.pathname?.startsWith("/cities/")) {
        const prefill = localStorage.getItem("pulpit_prefill_from_city")
        if (prefill) {
          const option = await resolveCityToPlaceOption(prefill)
          if (option) {
            locationInitializedRef.current = true
            setFormValues((prev) => ({ ...prev, from: option }))
          }
        }
      }
    }

    run()
  }, [routerLocation.pathname, location?.city, resolveCityToPlaceOption, routePrefill])

  // suggestions
  useEffect(() => {
    const loadDefaultOptions = async () => {
      const currentCity = location?.city || formValues?.from?.city || formValues?.from?.label
      const fromCity = formValues?.from?.city || formValues?.from?.label
      const fromOptions = await getDefaultOptions("from", currentCity)
      const toOptions = await getDefaultOptions("to", null, fromCity || currentCity)
      setDefaultFromOptions(fromOptions || [])
      setDefaultToOptions(toOptions || [])
    }
    loadDefaultOptions()
  }, [location?.city, formValues?.from])

  const extractCityFromText = (text = "") => (text.split(",")[0] || "").trim()

  const makeValueWithDetails = (value, details) => {
    const labelText = getLabel(value)
    return {
      ...value,
      latitude: details?.lat ?? null,
      longitude: details?.lng ?? null,
      city: details?.city || extractCityFromText(labelText), // ✅ fallback
      fullAddress: details?.fullAddress || labelText,
    }
  }

  const handleTypeChange = (e) => {
    const { value } = e.target
    setFormValues((prev) => ({ ...prev, type: value }))
    const isRound = value === "round trip"
    setShowReturn(isRound)
    if (!isRound) {
      setFormValues((prev) => ({ ...prev, returnDate: "", returnTime: "" }))
    }
  }

  const handlePlaceSelect = async (value, field) => {
    hasInteractedRef.current = true

    if (!value) {
      setFormValues((prev) => ({ ...prev, [field]: null }))

      if (field === "from") {
        setLocationDetails((prev) => ({
          ...prev,
          pickup_lat: null,
          pickup_long: null,
          pickup_city: "",
          pickup_fullAddress: "",
        }))
        setFormValues((prev) => ({ ...prev, to: null }))
        setLocationDetails((prev) => ({
          ...prev,
          drop_lat: null,
          drop_long: null,
          drop_city: "",
          drop_fullAddress: "",
        }))
      } else {
        setLocationDetails((prev) => ({
          ...prev,
          drop_lat: null,
          drop_long: null,
          drop_city: "",
          drop_fullAddress: "",
        }))
      }
      return
    }

    if (field === "to" && formValues.from && isSameLocation?.(value, formValues.from)) {
      setFormValues((prev) => ({ ...prev, to: null }))
      toast.error("Pickup and drop locations cannot be the same")
      return
    }

    const placeId = value?.value?.place_id || value?.place_id
    const details = placeId ? await getPlaceDetailsByPlaceId(placeId) : null
    const nextValue = makeValueWithDetails(value, details)

    setFormValues((prev) => ({ ...prev, [field]: nextValue }))
    saveRecentSearch(nextValue, field)

    if (field === "from") {
      setLocationDetails((prev) => ({
        ...prev,
        pickup_lat: nextValue.latitude,
        pickup_long: nextValue.longitude,
        pickup_city: nextValue.city,
        pickup_fullAddress: nextValue.fullAddress,
      }))

      // update to defaults based on from-city
      const toOptions = await getDefaultOptions("to", null, nextValue.city || location?.city)
      setDefaultToOptions(toOptions || [])

      setFromMenuIsOpen(false)
      setToMenuIsOpen(true)
      setTimeout(() => toSelectRef.current?.focus?.(), 120)
    } else {
      setLocationDetails((prev) => ({
        ...prev,
        drop_lat: nextValue.latitude,
        drop_long: nextValue.longitude,
        drop_city: nextValue.city,
        drop_fullAddress: nextValue.fullAddress,
      }))

      setToMenuIsOpen(false)
      setTimeout(() => pickUpDateRef.current?.open?.(), 150)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!formValues.from) {
      return toast.error("Please select a pickup location")
    }

    if (!formValues.to) {
      return toast.error("Please select a drop location")
    }

    if (isSameLocation?.(formValues.from, formValues.to)) {
      return toast.error("Pickup and drop locations cannot be the same")
    }

    if (!formValues.pickUpDate) {
      return toast.error("Please select pickup date")
    }

    if (!formValues.pickUpTime) {
      return toast.error("Please select pickup time")
    }

    if (formValues.type === "round trip") {
      if (!formValues.returnDate) {
        return toast.error("Please select return date")
      }

      if (!formValues.returnTime) {
        return toast.error("Please select return time")
      }
    }

    if (!formValues.mobileNumber) {
      return toast.error("Please enter mobile number")
    }

    if (!isValidMobileNumber(formValues.mobileNumber)) {
      return toast.error("Enter a valid 10-digit mobile number (cannot start with 0)")
    }

    const pickup_city =
      locationDetails.pickup_city ||
      formValues.from?.city ||
      extractCityFromText(getLabel(formValues.from)) ||
      ""

    const drop_city =
      locationDetails.drop_city ||
      formValues.to?.city ||
      extractCityFromText(getLabel(formValues.to)) ||
      ""

    if (!pickup_city || !drop_city) {
      return toast.error("Please select valid From/To cities")
    }

    if (submitLockRef.current || searchLoading) {
      return
    }

    submitLockRef.current = true
    setSearchLoading(true)

    // Clear old route result only when a new valid search starts.
    localStorage.removeItem("cabSearchResult")
    localStorage.removeItem("tripData")

    const safetyTimeout = setTimeout(() => {
      submitLockRef.current = false
      setSearchLoading(false)
    }, 15000)

    // The autofilled "From" city and the instant To/From suggestions are city
    // NAMES with no coordinates, but the fare API prices by lat/long. Resolve any
    // missing coords from the city name before searching, so a valid city-to-city
    // trip (e.g. Ahmedabad -> Surat) completes instead of dead-ending.
    let pickupLat = locationDetails.pickup_lat
    let pickupLong = locationDetails.pickup_long
    let dropLat = locationDetails.drop_lat
    let dropLong = locationDetails.drop_long
    if (mapsReady) {
      if ((pickupLat == null || pickupLong == null) && pickup_city) {
        const g = await geocodeCity(pickup_city)
        if (g) { pickupLat = g.lat; pickupLong = g.lng }
      }
      if ((dropLat == null || dropLong == null) && drop_city) {
        const g = await geocodeCity(drop_city)
        if (g) { dropLat = g.lat; dropLong = g.lng }
      }
    }

    const commonTripData = {
      trip_type: 3,
      round_trip: formValues.type === "round trip" ? 1 : 0,

      pickup: locationDetails.pickup_fullAddress || getLabel(formValues.from) || "",
      drop: locationDetails.drop_fullAddress || getLabel(formValues.to) || "",

      pickup_lat: pickupLat,
      pickup_long: pickupLong,
      drop_lat: dropLat,
      drop_long: dropLong,

      package_type_in_hours: "",
      trip_date: formValues.pickUpDate,
      trip_time: formValues.pickUpTime,
      phone_number: formValues.mobileNumber,
      ip_address: location?.ip || "8425",

      return_date: formValues.returnDate,
      return_time: formValues.returnTime,
    }

    // API payload: keep city empty so backend returns default 11/km fare.
    const apiTripData = {
      ...commonTripData,
      pickup_city: "",
      drop_city: "",
    }

    // UI / route storage: keep real city names so /routes/:from-to-to-cab opens properly.
    const routeTripData = {
      ...commonTripData,
      pickup_city,
      drop_city,
    }

    try {
      const response = await dispatch(getCabs(apiTripData)).unwrap()

      const cabList = response?.data?.data || []
      if (cabList.length === 0) {
        toast.info("No cabs available for the selected route/time.")
      }

      localStorage.setItem("tripData", JSON.stringify(routeTripData))
      localStorage.setItem("cabSearchResult", JSON.stringify(response?.data ?? {}))
      dispatchTripDataUpdated()

      const from = slugify(pickup_city)
      const to = slugify(drop_city)
      const routePath = `${from}-to-${to}-cab`

      saveRecentTrip({ trip_type: 3, fromCity: pickup_city, toCity: drop_city })
      navigate(`/routes/${routePath}/`, { state: { tripData: routeTripData } })
    } catch (err) {
      const msg =
        err && typeof err === "string"
          ? err
          : err?.message || "Something went wrong"

      const isRateLimit =
        typeof msg === "string" &&
        (msg.includes("Rate limit") || msg.includes("Maximum"))

      toast.error(
        isRateLimit
          ? "You've exceeded the request limit. Please try again after a few hours."
          : msg
      )
    } finally {
      clearTimeout(safetyTimeout)
      submitLockRef.current = false
      setSearchLoading(false)
    }
  }

  const MIN_RETURN_GAP_HOURS = 12

  const earliestReturn = useMemo(() => {
    if (!formValues.pickUpDate || !formValues.pickUpTime) return null
    const [h, m] = formValues.pickUpTime.split(":").map(Number)
    if (isNaN(h)) return null
    const d = new Date(formValues.pickUpDate)
    d.setHours(h, m || 0, 0, 0)
    return new Date(d.getTime() + MIN_RETURN_GAP_HOURS * 60 * 60 * 1000)
  }, [formValues.pickUpDate, formValues.pickUpTime])

  const returnMinDate = earliestReturn
    ? new Date(earliestReturn.getFullYear(), earliestReturn.getMonth(), earliestReturn.getDate())
    : formValues.pickUpDate
      ? new Date(formValues.pickUpDate)
      : new Date(formattedToday)

  const returnMinTime = earliestReturn && formValues.returnDate
    ? (() => {
        const retDate = new Date(formValues.returnDate)
        const minDate = new Date(earliestReturn.getFullYear(), earliestReturn.getMonth(), earliestReturn.getDate())
        if (retDate.getTime() === minDate.getTime()) {
          const rh = earliestReturn.getHours()
          const rm = earliestReturn.getMinutes()
          const rounded = rm > 0 ? (Math.ceil(rm / 30) * 30) : 0
          const finalH = rounded >= 60 ? rh + 1 : rh
          const finalM = rounded >= 60 ? 0 : rounded
          if (finalH >= 24) return "23:30"
          return `${String(finalH).padStart(2, "0")}:${String(finalM).padStart(2, "0")}`
        }
        return null
      })()
    : null

  useEffect(() => {
    if (!earliestReturn || !formValues.returnDate) return
    const retDate = new Date(formValues.returnDate)
    const retDateOnly = new Date(retDate.getFullYear(), retDate.getMonth(), retDate.getDate())
    const minDateOnly = new Date(earliestReturn.getFullYear(), earliestReturn.getMonth(), earliestReturn.getDate())
    if (retDateOnly < minDateOnly) {
      setFormValues((prev) => ({ ...prev, returnDate: "", returnTime: "" }))
      return
    }
    if (formValues.returnTime && retDateOnly.getTime() === minDateOnly.getTime()) {
      const [rh, rm] = formValues.returnTime.split(":").map(Number)
      const retMinutes = rh * 60 + (rm || 0)
      const minMinutes = earliestReturn.getHours() * 60 + earliestReturn.getMinutes()
      if (retMinutes < minMinutes) {
        setFormValues((prev) => ({ ...prev, returnTime: "" }))
      }
    }
  }, [earliestReturn, formValues.returnDate, formValues.returnTime])

  return (
    <div className="outstation-form">
      <form onSubmit={handleSubmit}>
        <div className="outstation-form__option-box">
          <div
            className={
              formValues.type === "one way"
                ? "outstation-form__option outstation-form__option-active"
                : "outstation-form__option"
            }
          >
            <input
              type="radio"
              className="input-radio"
              name="type"
              value="one way"
              checked={formValues.type === "one way"}
              onChange={handleTypeChange}
            />
            <span className="radio-button"></span>
            <svg className="outstation-form__option-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M5 12h14" /><path d="m13 6 6 6-6 6" />
            </svg>
            <label className="outstation-form__option-label">One Way</label>
          </div>

          <div
            className={
              formValues.type === "round trip"
                ? "outstation-form__option outstation-form__option-active"
                : "outstation-form__option"
            }
          >
            <input
              type="radio"
              className="input-radio"
              name="type"
              value="round trip"
              checked={formValues.type === "round trip"}
              onChange={handleTypeChange}
            />
            <span className="radio-button"></span>
            <svg className="outstation-form__option-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M3 9h14" /><path d="m14 6 3 3-3 3" /><path d="M21 15H7" /><path d="m10 18-3-3 3-3" />
            </svg>
            <label className="outstation-form__option-label">Round Trip</label>
          </div>
        </div>

        {!mapsReady && (
          <div className="outstation-form__maps-loading">
            <div style={{ height: "4.4rem", marginBottom: "1.5rem", border: "1px solid #000", borderRadius: "4px", display: "flex", alignItems: "center", padding: "0 10px", color: "#888", fontSize: "1.4rem" }}>
              Loading places...
            </div>
            <div style={{ height: "4.4rem", marginBottom: "1.5rem", border: "1px solid #000", borderRadius: "4px", display: "flex", alignItems: "center", padding: "0 10px", color: "#888", fontSize: "1.4rem" }}>
              Loading places...
            </div>
          </div>
        )}
        {/* key: react-select copies defaultOptions into state on its first
            render and never re-reads the prop, so a changed suggestion list only
            takes effect on remount. The Select is value-controlled, so
            remounting keeps the user's selection. */}
        {mapsReady && <GooglePlacesAutocomplete
          key={`from-${defaultFromOptions.map((o) => o.label).join("|")}`}
          debounce={400}
          minLengthAutocomplete={3}
          selectProps={{
            ref: fromSelectRef,
            value: formValues.from,
            onChange: (v) => handlePlaceSelect(v, "from"),
            placeholder: "From *",
            isClearable: true,
            defaultOptions: defaultFromOptions,

            menuIsOpen: fromMenuIsOpen,
            onFocus: () => {
              setFromMenuIsOpen(true)
              setToMenuIsOpen(false)
            },
            onBlur: () => setFromMenuIsOpen(false),
            onMenuClose: () => setFromMenuIsOpen(false),
            onInputChange: () => {
              setFromMenuIsOpen(true)
              setToMenuIsOpen(false)
            },

            getOptionLabel: (e) => e?.label || "",
            getOptionValue: (e) =>
              e?.value?.place_id || e?.place_id || e?.value?.description || e?.label || "",

            styles: {
              control: (p, state) => ({
                ...p,
                padding: "0 6px",
                border: state.isFocused ? "1.5px solid #21a5a0" : "1.5px solid #e6ecea",
                borderRadius: "1.2rem",
                background: state.isFocused ? "#ffffff" : "#f5f9f8",
                boxShadow: state.isFocused ? "0 0 0 4px #e9f6f5" : "none",
                width: "100%",
                height: "4.4rem",
                minHeight: "4.4rem",
                display: "flex",
                alignItems: "center",
                marginBottom: "1rem",
                fontSize: "1.4rem",
                transition: "border-color .18s, box-shadow .18s, background .18s",
              }),
              input: (p) => ({ ...p, color: "#152321", fontSize: "1.4rem" }),
              placeholder: (p) => ({ ...p, color: "#71817f", fontSize: "1.4rem" }),
              menu: (p) => ({ ...p, fontSize: "1.4rem", zIndex: 3, borderRadius: "1.2rem", overflow: "hidden" }),
            },
            components: { DropdownIndicator: () => null, IndicatorSeparator: () => null },
          }}
          autocompletionRequest={{ componentRestrictions: { country: "in" } }}
        />}

        {mapsReady && <GooglePlacesAutocomplete
          key={`to-${defaultToOptions.map((o) => o.label).join("|")}`}
          debounce={400}
          minLengthAutocomplete={3}
          selectProps={{
            ref: toSelectRef,
            value: formValues.to,
            onChange: (v) => handlePlaceSelect(v, "to"),
            placeholder: "To *",
            isClearable: true,
            defaultOptions: defaultToOptions,

            menuIsOpen: toMenuIsOpen,
            onFocus: () => {
              setToMenuIsOpen(true)
              setFromMenuIsOpen(false)
            },
            onBlur: () => setToMenuIsOpen(false),
            onMenuClose: () => setToMenuIsOpen(false),
            onInputChange: () => {
              setToMenuIsOpen(true)
              setFromMenuIsOpen(false)
            },

            getOptionLabel: (e) => e?.label || "",
            getOptionValue: (e) =>
              e?.value?.place_id || e?.place_id || e?.value?.description || e?.label || "",

            styles: {
              control: (p, state) => ({
                ...p,
                padding: "0 6px",
                border: state.isFocused ? "1.5px solid #21a5a0" : "1.5px solid #e6ecea",
                borderRadius: "1.2rem",
                background: state.isFocused ? "#ffffff" : "#f5f9f8",
                boxShadow: state.isFocused ? "0 0 0 4px #e9f6f5" : "none",
                width: "100%",
                height: "4.4rem",
                minHeight: "4.4rem",
                display: "flex",
                alignItems: "center",
                marginBottom: "1rem",
                fontSize: "1.4rem",
                transition: "border-color .18s, box-shadow .18s, background .18s",
              }),
              input: (p) => ({ ...p, color: "#152321", fontSize: "1.4rem" }),
              placeholder: (p) => ({ ...p, color: "#71817f", fontSize: "1.4rem" }),
              menu: (p) => ({ ...p, fontSize: "1.4rem", zIndex: 3, borderRadius: "1.2rem", overflow: "hidden" }),
            },
            components: { DropdownIndicator: () => null, IndicatorSeparator: () => null },
          }}
          autocompletionRequest={{ componentRestrictions: { country: "in" } }}
        />}

        <DateTimeProvider>
          <div className="outstation-form__date-time mb-5">
            <DatePickerInput
              ref={pickUpDateRef}
              value={formValues.pickUpDate ? new Date(formValues.pickUpDate) : null}
              minDate={new Date(formattedToday)}
              placeholder="Pick Up Date"
              onChange={(d) => {
                const newDate = d ? new Date(d).toLocaleDateString("en-CA") : ""
                setFormValues((prev) => {
                  const updated = { ...prev, pickUpDate: newDate, pickUpTime: "" }
                  if (prev.returnDate && newDate > prev.returnDate) {
                    updated.returnDate = ""
                    updated.returnTime = ""
                  }
                  return updated
                })
                setTimeout(() => pickUpTimeRef.current?.open?.(), 200)
              }}
            />

            <TimePickerInput
              ref={pickUpTimeRef}
              value={formValues.pickUpTime}
              placeholder="Pick Up Time"
              selectedDate={formValues.pickUpDate ? new Date(formValues.pickUpDate) : null}
              onChange={(t) => {
                setFormValues((prev) => ({ ...prev, pickUpTime: t || "" }))
                if (showReturn) setTimeout(() => returnDateRef.current?.open?.(), 200)
                else setTimeout(() => mobileNumberInputRef.current?.focus?.(), 150)
              }}
            />
          </div>

          {showReturn && (
            <div className="outstation-form__date-time mb-5">
              <DatePickerInput
                ref={returnDateRef}
                value={formValues.returnDate ? new Date(formValues.returnDate) : null}
                minDate={returnMinDate}
                placeholder="Return Date"
                onChange={(d) => {
                  const newReturn = d ? new Date(d).toLocaleDateString("en-CA") : ""
                  setFormValues((prev) => {
                    const updated = { ...prev, returnDate: newReturn }
                    if (newReturn !== prev.returnDate) updated.returnTime = ""
                    return updated
                  })
                  setTimeout(() => returnTimeRef.current?.open?.(), 200)
                }}
              />

              <TimePickerInput
                ref={returnTimeRef}
                value={formValues.returnTime}
                placeholder="Return Time"
                selectedDate={formValues.returnDate ? new Date(formValues.returnDate) : null}
                minTime={returnMinTime}
                onChange={(t) => {
                  setFormValues((prev) => ({ ...prev, returnTime: t || "" }))
                  setTimeout(() => mobileNumberInputRef.current?.focus?.(), 150)
                }}
              />
            </div>
          )}
        </DateTimeProvider>

        <div className="input-phone-wrapper mb-5">
          <div
            className={`input-phone-box${isValidMobileNumber(formValues.mobileNumber) ? " input-phone-box--valid" : formValues.mobileNumber.length > 0 ? " input-phone-box--invalid" : ""}`}
          >
            <span className="phone-code">+91</span>
            <span className="phone-divider"></span>

            <input
              type="tel"
              className="phone-input"
              placeholder="Mobile Number"
              name="mobileNumber"
              value={formatMobileDisplay(formValues.mobileNumber)}
              onChange={(e) => {
                const v = parseMobileInput(e.target.value)
                setFormValues((p) => ({ ...p, mobileNumber: v }))
              }}
              maxLength={11}
              ref={mobileNumberInputRef}
              required
            />
          </div>
        </div>

<button type="submit" className="input-submit-button" disabled={searchLoading}>
        {searchLoading ? "Searching..." : "Search"}
      </button>
      </form>

      <p className="outstation-form__paragraph">
        Last cab booked from {randomCity} {lastBookedText}.
      </p>
    </div>
  )
}

export default OutstationForm